import { FC, ReactNode } from 'react';

import Box from '@o2/ui/Box';
import Paper, { PaperProps } from '@o2/ui/Paper';
import Stack, { StackProps } from '@o2/ui/Stack';
import Typography from '@o2/ui/Typography';

export interface LinkBoxListProps extends PaperProps {
  actionButton?: ReactNode;
  children?: ReactNode;
  /**
   * Disable styling the passed `heading` as a heading.
   *
   * Defaults to false (= does style) if `heading` is a string,
   * true otherwise (if you pass a ReactElement, for example, it will not be styled).
   *
   * Useful to override for example when composing the text content from several `<span/>`s.
   */
  disableHeadingStyling?: boolean;
  /**
   * Props to pass to the Stack containing `heading` and `actionButton`.
   */
  headProps?: Partial<StackProps>;
  /**
   * If a string, it will be wrapped in a h5 Typography (unless `disableHeadingStyling` is true). For customization, you can
   * pass any ReactNode, such as your own Typography instance.
   */
  heading?: ReactNode;
  itemSpacing?: StackProps['spacing'];
}

const LinkBoxList: FC<LinkBoxListProps> = ({
  actionButton,
  children,
  headProps,
  heading,
  disableHeadingStyling = typeof heading !== 'string',
  itemSpacing = 1,
  ...props
}) => {
  return (
    <Paper {...props}>
      {(heading || actionButton) && (
        <Stack mb={2} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" {...headProps}>
          {disableHeadingStyling ? heading : <Typography variant="h5">{heading}</Typography>}
          {actionButton && <Box>{actionButton}</Box>}
        </Stack>
      )}
      <Stack direction="column" spacing={itemSpacing}>
        {children}
      </Stack>
    </Paper>
  );
};

export default LinkBoxList;
